import Phaser from "phaser";
import type { GameApp, ChoiceCtx } from "../app/GameApp";
import { REGISTRY_KEYS } from "../app/GameApp";
import type { ChoiceStep, Stat } from "../core/roscha";
import { CARD_CATALOG } from "../core/roscha";
import { renderGameCard } from "./TavernOverlay";
import { mountOverlay, type DomOverlay } from "@/ui/domHelpers";

const STAT_LABEL: Record<Stat, string> = {
  strength: "Сила",
  dexterity: "Ловкость",
  intelligence: "Интеллект",
  wisdom: "Мудрость",
  constitution: "Телосложение",
};

export class ChoiceOverlay extends Phaser.Scene {
  private app!: GameApp;
  private ctx?: ChoiceCtx;
  private overlay?: DomOverlay;
  private stepIndex = 0;

  constructor() {
    super("ChoiceOverlay");
  }

  create() {
    this.app = this.game.registry.get("app") as GameApp;

    const apply = (ctx: ChoiceCtx | null) => {
      this.overlay?.destroy();
      this.overlay = undefined;
      this.ctx = ctx ?? undefined;
      if (!this.ctx) return;
      this.stepIndex = 0;
      this.renderStep();
    };

    apply(this.registry.get(REGISTRY_KEYS.currentChoice) as ChoiceCtx | null);
    const onChange = (_p: unknown, value: ChoiceCtx | null) => apply(value);
    this.registry.events.on(`changedata-${REGISTRY_KEYS.currentChoice}`, onChange);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.registry.events.off(`changedata-${REGISTRY_KEYS.currentChoice}`, onChange);
    });
  }

  private renderStep() {
    if (!this.ctx) return;
    const step = this.ctx.steps[this.stepIndex];
    if (!step) {
      this.finish();
      return;
    }
    this.overlay?.destroy();
    this.overlay = mountOverlay(this, {
      title: "Новый уровень",
      subtitle: step.title,
      bodyHtml: this.buildBody(step),
      onClose: () => this.finish(),
    });
    this.wireButtons(step);
  }

  private buildBody(step: ChoiceStep): string {
    const hero = this.app.getWorldState()?.hero;
    const stats = hero?.stats;
    const opts = step.options.map((opt, idx) => {
      if (opt.choice.kind === "addCard" && stats) {
        const card = CARD_CATALOG[opt.choice.cardId];
        const inner = card ? renderGameCard(card, stats, undefined, undefined, hero?.domainId ?? undefined) : `<span>${opt.label}</span>`;
        return `<button class="levelup__pick levelup__pick--card" data-pick="${idx}">
          <span class="levelup__card-flip"><span class="levelup__card-face">${inner}</span></span>
        </button>`;
      }
      const label = opt.choice.kind === "statPlusOne" ? `+1 ${STAT_LABEL[opt.choice.stat]}` : opt.label;
      return `<button class="levelup__pick levelup__pick--stat" data-pick="${idx}">
        <span class="levelup__pick-stat">${label}</span>
      </button>`;
    }).join("");
    return `<div class="levelup__options">${opts}</div>`;
  }

  private wireButtons(step: ChoiceStep) {
    if (!this.overlay) return;
    this.overlay.root.querySelectorAll<HTMLElement>("[data-pick]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const idx = Number(btn.dataset.pick);
        const opt = step.options[idx];
        if (!opt) return;
        if (opt.choice.kind === "addCard" && !btn.classList.contains("is-revealed")) {
          btn.classList.add("is-revealed");
          return;
        }
        this.ctx?.onPick(opt.choice);
        this.stepIndex += 1;
        this.renderStep();
      });
    });
  }

  private finish() {
    this.overlay?.destroy();
    this.overlay = undefined;
    const done = this.ctx?.onDone;
    this.ctx = undefined;
    done?.();
  }

  shutdown() {
    this.overlay = undefined;
  }
}
